// Cliente HTTP de la SPA (ARCHITECTURE §9, API §1). Mismo origen, cookie de sesión, CSRF por header,
// claves de idempotencia, errores tipados con el mensaje en español de la API y manejo central de 401/403.
import { setServerNow } from './time';
import { t } from './i18n/es-AR';

export const API_BASE = '/api/v1';
export const PUBLIC_API_BASE = '/api/public';

const VIEW_AS_KEY = 'atte.viewAs';
const DEFAULT_TIMEOUT_MS = 30_000;

// ─────────────────────────────── errores ───────────────────────────────

/** Códigos de la API (API §1.7) más los del cliente (`network`, `timeout`). */
export type ApiErrorCode =
  | 'validation_error'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'module_disabled'
  | 'rate_limited'
  | 'invalid_state'
  | 'internal_error'
  | 'unavailable'
  | 'network'
  | 'timeout'
  | 'unknown';

export interface ApiErrorInit {
  status: number;
  code: ApiErrorCode;
  message: string;
  details?: Record<string, unknown>;
  method?: string;
  path?: string;
  retryAfterSeconds?: number;
}

/** Error de la API con el mensaje para mostrar tal cual (UX-P10). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: Record<string, unknown>;
  readonly method?: string;
  readonly path?: string;
  readonly retryAfterSeconds?: number;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? {};
    this.method = init.method;
    this.path = init.path;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }

  /** `details.reason` (motivo estable: `bad_credentials`, `slot_taken`, `mfa_pending`…). */
  get reason(): string | undefined {
    const r = this.details.reason;
    return typeof r === 'string' ? r : undefined;
  }

  /** Errores por campo (`details.fields`), para react-hook-form. */
  get fields(): Record<string, string> {
    const f = this.details.fields;
    if (!f || typeof f !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(f as Record<string, unknown>)) if (typeof v === 'string') out[k] = v;
    return out;
  }

  /** Sin red o tiempo agotado. */
  get isNetwork(): boolean {
    return this.code === 'network' || this.code === 'timeout';
  }

  /** Vale la pena reintentar solo (red, 5xx, 503). */
  get isTransient(): boolean {
    return this.isNetwork || this.status >= 500;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Mensaje para mostrar de cualquier error. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message;
  return t.errors.internal;
}

const KNOWN_CODES = new Set<ApiErrorCode>([
  'validation_error',
  'unauthenticated',
  'forbidden',
  'not_found',
  'conflict',
  'module_disabled',
  'rate_limited',
  'invalid_state',
  'internal_error',
  'unavailable',
]);

function fallbackMessage(status: number): { code: ApiErrorCode; message: string } {
  if (status === 401) return { code: 'unauthenticated', message: t.errors.sessionExpired };
  if (status === 403) return { code: 'forbidden', message: t.errors.forbiddenAction };
  if (status === 404) return { code: 'not_found', message: 'No encontramos lo que buscabas.' };
  if (status === 429) return { code: 'rate_limited', message: t.errors.rateLimited };
  if (status === 503 || status === 502 || status === 504) return { code: 'unavailable', message: t.errors.unavailable };
  if (status >= 500) return { code: 'internal_error', message: t.errors.internal };
  return { code: 'unknown', message: t.errors.internal };
}

// ─────────────────────────────── ganchos globales ───────────────────────────────

export interface ApiHandlers {
  /** 401 fuera de los endpoints de ingreso: sesión vencida o revocada. */
  onUnauthorized?: (err: ApiError) => void;
  /** 401 `mfa_pending`: falta el segundo paso. */
  onMfaPending?: (err: ApiError) => void;
  /** 403 `module_disabled` (un módulo se apagó con la pantalla abierta). */
  onModuleDisabled?: (err: ApiError) => void;
  /** 403 `forbidden/password_change_required` o `mfa_setup_required`: sesión restringida. */
  onRestricted?: (err: ApiError) => void;
  /** 403 `forbidden/view_as`: la vista de solo lectura ya no está disponible. */
  onViewAsInvalid?: (err: ApiError) => void;
}

let handlers: ApiHandlers = {};

/** Lo llama la app al montar el router (navegación y avisos). */
export function configureApi(next: ApiHandlers): () => void {
  handlers = { ...handlers, ...next };
  return () => {
    handlers = {};
  };
}

// ─────────────────────────────── vista de solo lectura ───────────────────────────────

/** Usuario que el dueño está "viendo como" (header `X-Atte-View-As` en GET/HEAD, API §1.4). */
export function getViewAsUserId(): string | null {
  try {
    return window.sessionStorage.getItem(VIEW_AS_KEY);
  } catch {
    return null;
  }
}

export function setViewAsUserId(userId: string | null): void {
  try {
    if (userId) window.sessionStorage.setItem(VIEW_AS_KEY, userId);
    else window.sessionStorage.removeItem(VIEW_AS_KEY);
  } catch {
    /* almacenamiento bloqueado */
  }
}

// ─────────────────────────────── idempotencia ───────────────────────────────

/** Clave nueva para `Idempotency-Key` (UUID; reintentos de la misma operación reusan la misma). */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Clave estable por intento de operación: la misma mientras no se llame `reset()` (al terminar bien o al
 * cambiar los datos del formulario). Así "Reintentar" después de un error de red no duplica un cobro.
 */
export function createIdempotencyKeyHolder() {
  let key = newIdempotencyKey();
  return {
    get: () => key,
    reset: () => {
      key = newIdempotencyKey();
    },
  };
}

// ─────────────────────────────── pedidos ───────────────────────────────

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  /** `true` genera una clave nueva; un string la usa tal cual. */
  idempotencyKey?: string | true;
  headers?: Record<string, string>;
  /** No disparar los ganchos globales de 401/403 (pantallas de ingreso, arranque). */
  skipGlobalHandlers?: boolean;
  timeoutMs?: number;
  /** Devuelve la `Response` sin leer (descargas). */
  raw?: boolean;
}

export type HttpMethod = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Arma la URL: rutas relativas a `/api/v1` salvo que empiecen con `/api/`. */
export function buildUrl(path: string, query?: Record<string, QueryValue>, base = API_BASE): string {
  const p = path.startsWith('/api/') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return p;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) for (const item of v) sp.append(k, String(item));
    else sp.append(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `${p}?${qs}` : p;
}

function linkSignals(outer: AbortSignal | undefined, timeoutMs: number) {
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, timeoutMs);
  const onAbort = () => ctrl.abort();
  if (outer) {
    if (outer.aborted) ctrl.abort();
    else outer.addEventListener('abort', onAbort, { once: true });
  }
  return {
    signal: ctrl.signal,
    timedOut: () => timedOut,
    done: () => {
      clearTimeout(timer);
      outer?.removeEventListener('abort', onAbort);
    },
  };
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return undefined;
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('application/json')) {
    const text = await res.text();
    return text ? (JSON.parse(text) as unknown) : undefined;
  }
  return res.text();
}

function toApiError(res: Response, body: unknown, method: string, path: string): ApiError {
  const retryAfter = Number(res.headers.get('retry-after'));
  const fb = fallbackMessage(res.status);
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error: { code?: string; message?: string; details?: Record<string, unknown> } }).error;
    const code = e.code && KNOWN_CODES.has(e.code as ApiErrorCode) ? (e.code as ApiErrorCode) : fb.code;
    return new ApiError({
      status: res.status,
      code,
      message: e.message || fb.message,
      details: e.details,
      method,
      path,
      retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    });
  }
  return new ApiError({ status: res.status, ...fb, method, path });
}

function runGlobalHandlers(err: ApiError) {
  if (err.status === 401) {
    if (err.reason === 'mfa_pending') handlers.onMfaPending?.(err);
    else handlers.onUnauthorized?.(err);
  } else if (err.status === 403) {
    if (err.code === 'module_disabled') handlers.onModuleDisabled?.(err);
    else if (err.reason === 'password_change_required' || err.reason === 'mfa_setup_required') handlers.onRestricted?.(err);
    else if (err.reason === 'view_as') handlers.onViewAsInvalid?.(err);
  }
}

/** Pedido genérico. Lanza `ApiError` ante cualquier respuesta no 2xx o falla de red. */
export async function request<T>(method: HttpMethod, path: string, opts: RequestOptions = {}, base = API_BASE): Promise<T> {
  const url = buildUrl(path, opts.query, base);
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  const isWrite = method !== 'GET' && method !== 'HEAD';
  if (isWrite) headers['X-Atte-Request'] = '1';
  if (opts.body !== undefined && !(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey === true ? newIdempotencyKey() : opts.idempotencyKey;
  if (!isWrite && base === API_BASE) {
    const viewAs = getViewAsUserId();
    if (viewAs) headers['X-Atte-View-As'] = viewAs;
  }

  const link = linkSignals(opts.signal, opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      body:
        opts.body === undefined ? undefined : opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body),
      signal: link.signal,
    });
  } catch (e) {
    link.done();
    if (opts.signal?.aborted) throw e; // cancelación de TanStack Query: se propaga tal cual
    throw new ApiError({
      status: 0,
      code: link.timedOut() ? 'timeout' : 'network',
      message: link.timedOut() ? t.errors.timeout : t.errors.network,
      method,
      path,
    });
  }
  link.done();

  const serverNowHeader = res.headers.get('x-server-now');
  if (serverNowHeader) setServerNow(serverNowHeader);

  if (opts.raw && res.ok) return res as unknown as T;

  let body: unknown;
  try {
    body = await parseBody(res);
  } catch {
    body = undefined;
  }

  if (!res.ok) {
    const err = toApiError(res, body, method, path);
    if (!opts.skipGlobalHandlers) runGlobalHandlers(err);
    throw err;
  }
  return body as T;
}

function client(base: string) {
  return {
    get: <T>(path: string, opts?: Omit<RequestOptions, 'body'>) => request<T>('GET', path, opts, base),
    post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
      request<T>('POST', path, { ...opts, body }, base),
    put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
      request<T>('PUT', path, { ...opts, body }, base),
    patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
      request<T>('PATCH', path, { ...opts, body }, base),
    delete: <T>(path: string, opts?: Omit<RequestOptions, 'body'>) => request<T>('DELETE', path, opts, base),
  };
}

/** API de la aplicación (`/api/v1`). */
export const api = client(API_BASE);
/** API pública de reservas (`/api/public`, sin sesión). */
export const publicApi = client(PUBLIC_API_BASE);

/**
 * Trae todas las páginas de una lista paginada (`Page<T>`) en lotes de 200, para exportar (API §1.11).
 * `onProgress(cargadas, total)` permite mostrar "Exportando… 1.200 de 5.400".
 */
export async function fetchAllPages<T>(
  path: string,
  query: Record<string, QueryValue> = {},
  onProgress?: (loaded: number, total: number) => void,
): Promise<T[]> {
  const pageSize = 200;
  const out: T[] = [];
  for (let page = 1; page < 10_000; page++) {
    const res = await api.get<{ items: T[]; total: number }>(path, { query: { ...query, page, pageSize } });
    out.push(...res.items);
    onProgress?.(out.length, res.total);
    if (res.items.length < pageSize || out.length >= res.total) break;
  }
  return out;
}
